import type { OrderDetails } from '@/entities/order/model'

export interface OrderTransitionDeps {
  acknowledge: (order: OrderDetails) => Promise<unknown>
  updateStatus: (params: { orderId: string, newStatus: string }) => Promise<unknown>
}

/**
 * Domain rule for advancing one or more orders to a new status.
 *
 * An order that is `Arrived` and not yet acknowledged must be acknowledged
 * before it can transition. If acknowledgement fails, that order does NOT
 * transition. Each order is handled independently, so a failure for one order
 * never blocks the others. Failures are collected and logged, never thrown.
 */
export async function transitionOrders(
  orders: OrderDetails[],
  newStatus: string,
  deps: OrderTransitionDeps,
): Promise<void> {
  const results = await Promise.allSettled(
    orders.map(order => transitionOne(order, newStatus, deps)),
  )

  const errors = results
    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    .map(result => result.reason)

  if (errors.length > 0) {
    console.error('Some order transitions failed:', errors)
  }
}

async function transitionOne(
  order: OrderDetails,
  newStatus: string,
  deps: OrderTransitionDeps,
): Promise<void> {
  if (order.status === 'Arrived' && !order.isAcknowledged) {
    await deps.acknowledge(order)
  }
  await deps.updateStatus({ orderId: order.orderId, newStatus })
}
